/**
 * Öne çıkan (hero) içerik bandı — sinematik sürüm (Tasarım V2, Dalga 10).
 *
 * ## B-1 (kırık görsel) — `HeroBanner` de aynı hatayı taşıyordu
 *
 * Poster `<img>`'si `onError` ile gizleniyordu; geriye **kırık resim ikonu**
 * veya boş kutu kalıyordu. Gerçek ekran görüntüsünde hero posterinde Chrome'un
 * bozuk resim simgesi göründü. `ItemCard` ile aynı düzeltme uygulandı:
 * iskelet + monogram rozeti.
 *
 * ## B-25 · Poster kutusu → sinematik arka plan
 *
 * Önceden sağda 200 px'lik ayrı bir poster kutusu vardı; geri kalan alan
 * boştu ve hero "panel" gibi görünüyordu. Artık kapak görseli **tüm hero'yu
 * kaplayan** bulanık arka plan olur; içerik sol-altta durur.
 *
 * Görsel yoksa arka plan yerine **monogram degrade** + büyük tür silueti
 * gösterilir — hiçbir koşulda boş kutu kalmaz.
 *
 * ## Oynatma ikonu
 *
 * `▶` metni yerine gömülü SVG ikonu (`Icon.tsx`): metin karakteri platform
 * yazı tipine göre farklı genişlikte ve `flex` ortalamasında kayıyordu.
 */
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import type { Item } from '../api.ts';
import { displayTitle, formatDuration, progressRatio } from '../format.ts';
import { FilmIcon, MusicIcon, PlayIcon, TvIcon } from '../Icon.tsx';
import { resizeProxyImage } from '../imageSize.ts';
import { mediaCopy } from '../mediaCopy.ts';
import { isAudioItem } from '../mediaKind.ts';

/** Sunucu imzalı thumbnail varsa onu, yoksa göreli yolu kullan. */
function localBackdropUrl(item: Item): string {
  return item.thumbnailUrl ?? mediaUrl(`/api/items/${item.id}/thumbnail`);
}

import { mediaUrl } from '../mediaToken.ts';
import { monogramGradient, monogramOf } from '../monogram.ts';

const KIND_LABEL: Record<string, string> = {
  movie: 'Film',
  show: 'Dizi',
  season: 'Sezon',
  episode: 'Bölüm',
  music: 'Müzik',
};

type ThumbnailState = 'loading' | 'ready' | 'error';

/** İçeriğin türüne göre arka plan silueti. */
function kindMark(item: Item, isAudio: boolean): ReactNode {
  if (isAudio) return <MusicIcon size={72} />;
  if (item.kind === 'movie') return <FilmIcon size={72} />;
  return <TvIcon size={72} />;
}

export function HeroBanner({
  item,
  mediaKind = 'video',
  onWatch,
}: {
  item: Item;
  /** Kütüphane türü; uzantısız müzik başlıklarını doğru sınıflar. */
  mediaKind?: 'video' | 'music';
  onWatch: () => void;
}): ReactNode {
  const resumeMs = item.resumePositionMs ?? item.progress?.positionMs;
  const ratio = progressRatio(resumeMs, item.durationMs);
  const percent = Math.round(ratio * 100);

  const isAudio = mediaKind === 'music' || isAudioItem(item);
  const copy = mediaCopy(isAudio ? 'music' : 'video');
  const kindLabel = isAudio ? 'Müzik' : (KIND_LABEL[item.kind] ?? item.kind);

  const [thumbState, setThumbState] = useState<ThumbnailState>('loading');
  // Sesli içerikte `<img>` hiç basılmaz → olay tetiklenmez → iskelet sonsuza
  // kadar döner. Bu yüzden doğrudan "görsel yok" durumundan başlar.
  const thumb: ThumbnailState = isAudio ? 'error' : thumbState;

  /*
   * Kapak yükleme zaman aşımı.
   *
   * Ölçüm: kapak isteği **asıldığında** (hata vermek yerine beklemektesin)
   * `onError` hiç tetiklenmiyor ve iskelet kalıcı hâle geliyordu. Uzak
   * kütüphanelerde (tünel kapalı, disk erişilemiyor) bu durum normaldir.
   *
   * 8 saniye sonra iskelet, monogram degrade + tür siluetine dönüşür: hero
   * hiçbir koşulda "yükleniyor" takılı kalmaz.
   */
  useEffect(() => {
    if (isAudio || thumbState !== 'loading') return;
    const timer = window.setTimeout(() => setThumbState('error'), 8000);
    return () => window.clearTimeout(timer);
  }, [isAudio, thumbState]);

  /** Kalan süre: kullanıcı "50:57" değil, "38 dk kaldı" bilgisini ister. */
  const remainingMs = item.durationMs && resumeMs ? Math.max(0, item.durationMs - resumeMs) : null;

  const localBackdrop = localBackdropUrl(item);
  const backdropSrc = resizeProxyImage(item.backdropUrl ?? item.posterUrl, 'w780') ?? localBackdrop;

  return (
    <section className="hero" aria-label="Öne Çıkan İçerik">
      {/*
        Arka plan katmanı.

        Görsel hazır olduğunda kapak fotoğrafı bulanıklaştırılmış olarak tüm
        yüzeyi kaplar; yükleniyorken iskelet, hata durumunda monogram
        degrade + büyük tür silueti gösterilir.
      */}
      <div className="hero__backdrop" aria-hidden="true">
        {thumb !== 'error' && !isAudio && (
          <img
            src={backdropSrc}
            alt=""
            className="hero__backdrop-img"
            data-state={thumb}
            fetchPriority="high"
            decoding="async"
            onLoad={() => setThumbState('ready')}
            onError={(event) => {
              const image = event.currentTarget;
              if (backdropSrc !== localBackdrop && !image.src.endsWith(localBackdrop)) {
                image.src = localBackdrop;
                return;
              }
              setThumbState('error');
            }}
          />
        )}

        {thumb === 'loading' && !isAudio && <div className="hero__backdrop-skeleton" />}

        {thumb === 'error' && (
          <div
            className="hero__backdrop-fallback"
            style={{ background: monogramGradient(item.id) }}
          >
            <span className="hero__fallback-mark">{kindMark(item, isAudio)}</span>
            <span className="hero__fallback-text">{monogramOf(item.title)}</span>
          </div>
        )}
      </div>

      <div className="hero__backdrop-gradient" />

      <div className="hero__content">
        <div className="hero__badge">
          <span className="hero__badge-dot" />
          <span>
            {copy.resume} · {kindLabel}
          </span>
        </div>

        <h2 className="hero__title">{displayTitle(item)}</h2>

        <div className="hero__meta">
          {item.year !== null && <span className="hero__meta-chip">{item.year}</span>}
          {item.durationMs !== null && (
            <span className="hero__meta-chip">{formatDuration(item.durationMs)}</span>
          )}
          {remainingMs !== null && remainingMs > 0 && (
            <span className="hero__meta-chip hero__meta-chip--remaining">
              {formatDuration(remainingMs)} kaldı
            </span>
          )}
          {percent > 0 && (
            <span className="hero__meta-chip hero__meta-chip--accent">
              %{percent} {copy.progress}
            </span>
          )}
        </div>

        {ratio > 0 && ratio < 0.95 && (
          <div className="hero__progress-box">
            <div className="hero__progress-info">
              <span>{resumeMs ? formatDuration(resumeMs) : '0:00'}</span>
              <span>{item.durationMs ? formatDuration(item.durationMs) : ''}</span>
            </div>
            <div className="hero__progress-track" aria-hidden="true">
              <div className="hero__progress-fill" style={{ width: `${percent}%` }} />
            </div>
          </div>
        )}

        <div className="hero__actions">
          <button type="button" className="hero__btn-play" onClick={onWatch}>
            <span className="hero__play-icon" aria-hidden="true">
              <PlayIcon size={16} />
            </span>
            <span>{copy.resume}</span>
          </button>
          <a href={`#/oge/${item.id}`} className="hero__btn-details">
            Detaylar
          </a>
        </div>
      </div>
    </section>
  );
}
